/**
 * Locations — the manual location register, and the only way this fleet ever
 * learns where anything is.
 *
 * There is no GPS in any unit. Not "not yet integrated": there is no receiver.
 * Everything on this screen is somebody typing what they know, which is why the
 * assign form records who said it and the placement is history rather than a
 * single current field — "where was dro004 in March" is a question that gets
 * asked.
 *
 * The assign action is one POST, and the server does the close-and-open in a
 * single transaction. The client deliberately does not do it in two calls: a
 * dropped connection between them would leave a unit in two places or in none.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api/client';
import type { FleetUnit, Location } from '../api/types';
import { useAsync } from '../components/useAsync';
import { LivenessPill } from '../components/indicators';
import { DASH, stamp, unitKey, unitName } from '../components/format';

export function Locations() {
  const locations = useAsync(() => api.locations(), []);
  const fleet = useAsync(() => api.fleet(), []);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function reloadAll() {
    locations.reload();
    fleet.reload();
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Locations</h1>
        <span className="sub">
          Hand-entered. There is no GPS in any unit — if it is not recorded here, it is not
          known.
        </span>
        <div className="spacer" />
        <button onClick={reloadAll}>Refresh</button>
      </div>

      {error && <div className="note bad">{error}</div>}
      {message && <div className="note">{message}</div>}

      <div className="grid two">
        <div>
          <div className="panel">
            <header>Register</header>
            <div className="body flush">
              <table className="data">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Kind</th>
                    <th>Address</th>
                    <th className="num">Lat</th>
                    <th className="num">Lon</th>
                    <th className="num">Units here</th>
                  </tr>
                </thead>
                <tbody>
                  {(locations.data ?? []).map((l) => (
                    <tr key={l.id}>
                      <td style={{ fontWeight: 600 }}>{l.name}</td>
                      <td>{l.kind ?? <span className="nodata">{DASH}</span>}</td>
                      <td className="wrap">{l.address ?? <span className="nodata">{DASH}</span>}</td>
                      <td className="num">{l.latitude ?? DASH}</td>
                      <td className="num">{l.longitude ?? DASH}</td>
                      <td className="num">{l.units_present}</td>
                    </tr>
                  ))}
                  {locations.data?.length === 0 && (
                    <tr>
                      <td colSpan={6} className="hint" style={{ padding: 14 }}>
                        No locations yet. Add one on the right, then assign units to it.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="panel">
            <header>Where every unit is</header>
            <div className="body flush">
              <table className="data">
                <thead>
                  <tr>
                    <th>Unit</th>
                    <th>State</th>
                    <th>Location</th>
                    <th>Since</th>
                    <th>Move to</th>
                  </tr>
                </thead>
                <tbody>
                  {(fleet.data?.units ?? []).map((u) => (
                    <AssignRow
                      key={u.unit_id}
                      unit={u}
                      locations={locations.data ?? []}
                      onDone={(text) => {
                        setMessage(text);
                        setError(null);
                        reloadAll();
                      }}
                      onError={(text) => {
                        setError(text);
                        setMessage(null);
                      }}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div>
          <NewLocation
            onCreated={(l) => {
              setMessage(`Added ${l.name}.`);
              setError(null);
              locations.reload();
            }}
            onError={(text) => {
              setError(text);
              setMessage(null);
            }}
          />
        </div>
      </div>
    </div>
  );
}

function AssignRow({
  unit,
  locations,
  onDone,
  onError,
}: {
  unit: FleetUnit;
  locations: Location[];
  onDone: (message: string) => void;
  onError: (message: string) => void;
}) {
  const [target, setTarget] = useState<string>('');
  const [busy, setBusy] = useState(false);

  async function assign() {
    setBusy(true);
    try {
      const locationId = target === 'unknown' ? null : Number(target);
      const result = await api.placeUnit(unitKey(unit), { location_id: locationId });
      onDone(
        result.changed
          ? `${unitName(unit)} → ${result.placement.location_name ?? 'unknown'}` +
              (result.previous ? ` (was ${result.previous.location_name ?? 'unknown'})` : '')
          : `${unitName(unit)} was already there — nothing recorded.`,
      );
      setTarget('');
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr className={unit.liveness === 'never_reported' ? 'no-data' : undefined}>
      <td>
        <Link to={`/units/${unitKey(unit)}`} style={{ fontWeight: 600 }}>
          {unitName(unit)}
        </Link>
      </td>
      <td>
        <LivenessPill liveness={unit.liveness} />
      </td>
      <td>
        {unit.location_name ?? <span className="nodata">not recorded</span>}
      </td>
      <td>{unit.at_location_since ? stamp(unit.at_location_since) : DASH}</td>
      <td>
        <span className="row" style={{ gap: 5, flexWrap: 'nowrap' }}>
          <select value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">choose…</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
            {/* Recording "it moved, we do not know where" beats leaving the
                register pointing at somewhere it has demonstrably left. */}
            <option value="unknown">— moved, destination unknown —</option>
          </select>
          <button className="small" disabled={!target || busy} onClick={() => void assign()}>
            {busy ? '…' : 'Assign'}
          </button>
        </span>
      </td>
    </tr>
  );
}

function NewLocation({
  onCreated,
  onError,
}: {
  onCreated: (l: Location) => void;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState('');
  const [kind, setKind] = useState('');
  const [address, setAddress] = useState('');
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const created = await api.createLocation({
        name: name.trim(),
        kind: kind.trim() || null,
        address: address.trim() || null,
        latitude: lat.trim() ? Number(lat) : null,
        longitude: lon.trim() ? Number(lon) : null,
        notes: notes.trim() || null,
      });
      onCreated(created);
      setName('');
      setKind('');
      setAddress('');
      setLat('');
      setLon('');
      setNotes('');
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel" onSubmit={submit}>
      <header>Add a location</header>
      <div className="body">
        <div className="grid" style={{ gap: 9 }}>
          <label className="field">
            Name
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className="field">
            Kind
            <input
              type="text"
              value={kind}
              placeholder="depot, site, workshop…"
              onChange={(e) => setKind(e.target.value)}
            />
          </label>
          <label className="field">
            Address
            <input type="text" value={address} onChange={(e) => setAddress(e.target.value)} />
          </label>
          <div className="row" style={{ gap: 9 }}>
            <label className="field" style={{ flex: 1 }}>
              Latitude
              <input
                type="number"
                step="0.000001"
                value={lat}
                onChange={(e) => setLat(e.target.value)}
              />
            </label>
            <label className="field" style={{ flex: 1 }}>
              Longitude
              <input
                type="number"
                step="0.000001"
                value={lon}
                onChange={(e) => setLon(e.target.value)}
              />
            </label>
          </div>
          <label className="field">
            Notes
            <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </label>
          <div className="row">
            <button className="primary" type="submit" disabled={busy || !name.trim()}>
              {busy ? 'Saving…' : 'Add location'}
            </button>
            <span className="hint">
              Coordinates are optional and are for a person reading a map, not for the units —
              they have no receiver.
            </span>
          </div>
        </div>
      </div>
    </form>
  );
}
